import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth.service';
import { 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Card,
    CardContent,
    LinearProgress,
    Tooltip,
    Alert
} from '@mui/material';
import {
    Computer as ComputerIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    FolderOpen as FolderIcon,
    Storage as StorageIcon,
    Schedule as ScheduleIcon,
    CheckCircle as ActiveIcon,
    Error as OfflineIcon,
    Warning as InterruptedIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

interface Machine {
    _id: string;
    machineId: string;
    machineName: string;
    userId: {
        _id: string;
        fullName: string;
        email: string;
    };
    os: string;
    status: 'active' | 'offline' | 'interrupted';
    lastSeen: string;
    lastLoginAt?: string;
    syncFolders: string[];
    storageUsed: number;
    filesCount: number;
    ipAddress?: string;
    agentVersion?: string;
    systemInfo?: {
        cpu: string;
        memory: number;
        diskSpace: number;
    };
    createdAt: string;
}

interface MachineStats {
    totalMachines: number;
    activeMachines: number;
    totalStorage: number;
    totalFiles: number;
}

const Machines: React.FC = () => {
    const { user } = useAuth();
    const [machines, setMachines] = useState<Machine[]>([]);
    const [stats, setStats] = useState<MachineStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchMachines();
        const interval = setInterval(fetchMachines, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchMachines = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.get('/machines');
            setMachines(response.data.machines);
            setStats(response.data.stats);
            setError(null);
        } catch (error) {
            console.error('Error fetching machines:', error);
            setError('Failed to load machines');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (machineId: string) => {
        if (!window.confirm('Are you sure you want to remove this machine?')) {
            return;
        }

        try {
            const api = authService.getApiInstance();
            await api.delete(`/machines/${machineId}`);
            fetchMachines();
        } catch (error) {
            console.error('Error deleting machine:', error);
            alert('Failed to delete machine');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active':
                return <ActiveIcon color="success" fontSize="small" />;
            case 'interrupted':
                return <InterruptedIcon color="warning" fontSize="small" />;
            case 'offline':
                return <OfflineIcon color="error" fontSize="small" />;
            default:
                return null;
        }
    };

    const getStatusChip = (status: string) => {
        const statusConfig: Record<string, { color: 'success' | 'warning' | 'error' | 'default' | 'primary' | 'secondary' | 'info', label: string }> = {
            active: { color: 'success', label: 'Active' },
            interrupted: { color: 'warning', label: 'Interrupted' },
            offline: { color: 'error', label: 'Offline' }
        };

        const config = statusConfig[status] || { color: 'default', label: status };

        const icon = getStatusIcon(status);
        
        return (
            <Chip
                icon={icon || undefined}
                label={config.label}
                color={config.color}
                size="small"
            />
        );
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleViewDetails = (machine: Machine) => {
        setSelectedMachine(machine);
        setDetailsOpen(true);
    };

    if (loading) {
        return (
            <div className="p-6">
                <LinearProgress />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-semibold text-gray-800">Machine Management</h1>
                <p className="text-gray-600 mt-2">Monitor and manage connected desktop agents</p>
            </div>

            {error && (
                <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Statistics Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Total Machines
                                    </Typography>
                                    <Typography variant="h4">
                                        {stats.totalMachines}
                                    </Typography>
                                </Box>
                                <ComputerIcon className="text-blue-500" />
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Active Now
                                    </Typography>
                                    <Typography variant="h4">
                                        {stats.activeMachines}
                                    </Typography>
                                </Box>
                                <ActiveIcon className="text-green-500" />
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Total Storage
                                    </Typography>
                                    <Typography variant="h5">
                                        {formatBytes(stats.totalStorage)}
                                    </Typography>
                                </Box>
                                <StorageIcon className="text-purple-500" />
                            </Box>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Total Files
                                    </Typography>
                                    <Typography variant="h4">
                                        {stats.totalFiles.toLocaleString()}
                                    </Typography>
                                </Box>
                                <FolderIcon className="text-orange-500" />
                            </Box>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Machines Table */}
            <Paper>
                <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Connected Machines</Typography>
                    <IconButton onClick={fetchMachines} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Machine Name</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>OS</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell>Last Seen</TableCell>
                                <TableCell align="center">Files</TableCell>
                                <TableCell>Storage</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {machines.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Box py={4}>
                                            <ComputerIcon className="text-gray-400 mb-2" style={{ fontSize: 48 }} />
                                            <Typography color="textSecondary">
                                                No machines connected yet
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                machines.map((machine) => (
                                    <TableRow key={machine._id} hover>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <ComputerIcon className="mr-2 text-gray-500" />
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {machine.machineName}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {machine.machineId}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{machine.userId.fullName}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {machine.userId.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{machine.os}</TableCell>
                                        <TableCell align="center">
                                            {getStatusChip(machine.status)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={format(new Date(machine.lastSeen), 'PPpp')}>
                                                <Typography variant="body2">
                                                    {format(new Date(machine.lastSeen), 'MMM d, h:mm a')}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="center">{machine.filesCount.toLocaleString()}</TableCell>
                                        <TableCell>{formatBytes(machine.storageUsed)}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Details">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleViewDetails(machine)}
                                                >
                                                    <FolderIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remove Machine">
                                                <IconButton 
                                                    size="small" 
                                                    color="error"
                                                    onClick={() => handleDelete(machine._id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Machine Details Dialog */}
            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
                {selectedMachine && (
                    <>
                        <DialogTitle>
                            <Box display="flex" alignItems="center">
                                <ComputerIcon className="mr-2" />
                                {selectedMachine.machineName}
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="subtitle2" color="textSecondary">Machine ID</Typography>
                                    <Typography variant="body1" gutterBottom>{selectedMachine.machineId}</Typography>
                                </div>
                                <div>
                                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                    <Box mb={1}>{getStatusChip(selectedMachine.status)}</Box>
                                </div>
                                <div>
                                    <Typography variant="subtitle2" color="textSecondary">Operating System</Typography>
                                    <Typography variant="body1" gutterBottom>{selectedMachine.os}</Typography>
                                </div>
                                <div>
                                    <Typography variant="subtitle2" color="textSecondary">IP Address</Typography>
                                    <Typography variant="body1" gutterBottom>{selectedMachine.ipAddress || 'N/A'}</Typography>
                                </div>
                                <div>
                                    <Typography variant="subtitle2" color="textSecondary">Agent Version</Typography>
                                    <Typography variant="body1" gutterBottom>{selectedMachine.agentVersion || 'N/A'}</Typography>
                                </div>
                                <div>
                                    <Typography variant="subtitle2" color="textSecondary">Last Login</Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedMachine.lastLoginAt 
                                            ? format(new Date(selectedMachine.lastLoginAt), 'PPpp')
                                            : 'N/A'}
                                    </Typography>
                                </div>
                                <div className="col-span-1 md:col-span-2">
                                    <Typography variant="subtitle2" color="textSecondary">Synced Folders</Typography>
                                    {selectedMachine.syncFolders && selectedMachine.syncFolders.length > 0 ? (
                                        <Box mt={1}>
                                            {selectedMachine.syncFolders.map((folder, index) => {
                                                // Make folder paths more readable
                                                const displayFolder = folder === '.' ? 'Current Directory' : 
                                                    folder.length > 50 ? '...' + folder.slice(-47) : folder;
                                                return (
                                                    <Chip
                                                        key={index}
                                                        label={displayFolder}
                                                        title={folder} // Show full path on hover
                                                        size="small"
                                                        icon={<FolderIcon />}
                                                        className="mr-1 mb-1"
                                                    />
                                                );
                                            })}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">No folders synced</Typography>
                                    )}
                                </div>
                                {selectedMachine.systemInfo && (
                                    <>
                                        <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <Typography variant="subtitle2" color="textSecondary">CPU</Typography>
                                                <Typography variant="body1">{selectedMachine.systemInfo.cpu}</Typography>
                                            </div>
                                            <div>
                                                <Typography variant="subtitle2" color="textSecondary">Memory</Typography>
                                                <Typography variant="body1">
                                                    {formatBytes(selectedMachine.systemInfo.memory)}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography variant="subtitle2" color="textSecondary">Disk Space</Typography>
                                                <Typography variant="body1">
                                                    {formatBytes(selectedMachine.systemInfo.diskSpace)}
                                                </Typography>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </div>
    );
};

export default Machines;